import React, { createContext, useCallback, useContext, useMemo } from "react";
import { ChartConfig } from "../models/interfaces";

interface ChartConfigContextProps {
  getChartConfig: (
    chart_type: string,
    view_type: string,
    grid: string,
    data_count?: number,
    bar_count?: number
  ) => Promise<ChartConfig["config"] | undefined>;
}

const ChartConfigContext = createContext<ChartConfigContextProps | undefined>(undefined);

export const ChartConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  // Cache for loaded configurations to avoid repeated imports
  const configCache = useMemo(() => new Map<string, ChartConfig[]>(), []);

  // Structured cache with config fingerprints
  const resolvedConfigCache = useMemo(() => ({
    // Cache by config identity + count ranges
    yAxis: new Map<string, any>(), // key: "configId-dataCount"
    xAxis: new Map<string, any>(), // key: "configId-barCount" 
    dataLabel: new Map<string, any>(), // key: "configId-barCount"
    barSize: new Map<string, any>(),
    fullConfig: new Map<string, ChartConfig["config"]>() // key: "configId-dataCount-barCount"
  }), []);

  // Create unique config fingerprint
  const getConfigFingerprint = useCallback((view_type: string, grid: string, chart_type: string) => {
    return `${view_type}::${grid}::${chart_type}`;
  }, []);

  const loadConfigFile = useCallback(async (view_type: string, grid: string): Promise<ChartConfig[] | null> => {
    const cacheKey = `${view_type}::${grid}`;

    // Return cached config if exists
    if (configCache.has(cacheKey)) {
      return configCache.get(cacheKey)!;
    }

    try {
      let config: ChartConfig[];

      // Determine the config file path based on view_type and grid
      if (view_type === 'default') {
        const defaultConfig = await import(`../../../utils/chartFormattingConfigs/default/config.json`);
        config = defaultConfig.default;
      } else if (view_type === 'mini') {
        const miniConfig = await import(`../../../utils/chartFormattingConfigs/mini/config.json`);
        config = miniConfig.default;
      } else {
        const gridConfig = await import(`../../../utils/chartFormattingConfigs/${view_type}/${grid}/config.json`);
        config = gridConfig.default;
      }

      // Cache the loaded config
      configCache.set(cacheKey, config);
      return config;

    } catch (error) {
      console.warn("Failed to load config", { view_type, grid, error });
      return null;
    }
  }, [configCache]);

  const resolveYAxisTickStyle = useCallback((tickStyles: any[], count: number | undefined, configId: string, position: string) => {
    if (!Array.isArray(tickStyles)) return undefined;

    // Create a cache key for this specific resolution
    const cacheKey = `${configId}-${position}-${count ?? 'undefined'}`;

    if (resolvedConfigCache.yAxis.has(cacheKey)) {
      return resolvedConfigCache.yAxis.get(cacheKey);
    }

    const result = tickStyles.find(style => {
      const min = style.min_data_count ?? -Infinity;
      const max = style.max_data_count ?? Infinity;
      return count !== undefined && count >= min && count <= max;
    }) ?? tickStyles[0];

    resolvedConfigCache.yAxis.set(cacheKey, result);
    return result;

  }, [resolvedConfigCache]);

  const resolveXAxisTickStyle = useCallback((tickStyles: any[], count: number | undefined, configId: string, position: string) => {
    if (!Array.isArray(tickStyles)) return undefined;
    // Cache key and lookup
    const cacheKey = `${configId}-${position}-${count ?? 'undefined'}`;

    if (resolvedConfigCache.xAxis.has(cacheKey)) {
      return resolvedConfigCache.xAxis.get(cacheKey);
    }

    const result = tickStyles.find(style => {
      const min = style.min_bar_count ?? -Infinity;
      const max = style.max_bar_count ?? Infinity;
      return count !== undefined && count >= min && count <= max;
    }) ?? tickStyles[0];

    resolvedConfigCache.xAxis.set(cacheKey, result);
    return result;
  }, [resolvedConfigCache]);

  const resolveDataLabel = useCallback((dataLabels: any[], count: number | undefined, configId: string) => {
    if (!Array.isArray(dataLabels)) return undefined;

    // Cache key and lookup
    const cacheKey = `${configId}-${count ?? 'undefined'}`;

    if (resolvedConfigCache.dataLabel.has(cacheKey)) {
      return resolvedConfigCache.dataLabel.get(cacheKey);
    }

    const result = dataLabels.find(label => {
      const min = label.min_bar_count ?? -Infinity;
      const max = label.max_bar_count ?? Infinity;
      return count !== undefined && count >= min && count <= max;
    }) ?? dataLabels[0];

    resolvedConfigCache.dataLabel.set(cacheKey, result);
    return result;
  }, [resolvedConfigCache]);

  const resolveBarSize = useCallback((barSizeStyles: any[], data_count?: number, bar_count?: number, configId?: string) => {
    if (!Array.isArray(barSizeStyles)) return undefined;
    // Create a cache key for this specific resolution
    const cacheKey = `${configId}-${data_count ?? 'undefined'}-${bar_count ?? 'undefined'}`;

    if (resolvedConfigCache.barSize.has(cacheKey)) {
      return resolvedConfigCache.barSize.get(cacheKey);
    }

    const result = barSizeStyles.find(style => {
      const countType = style.count_type;
      const min = style.min_count ?? -Infinity;
      const max = style.max_count ?? Infinity;

      if (countType === 'bar' && bar_count !== undefined) {
        return bar_count >= min && bar_count <= max;
      } else if (countType === 'data' && data_count !== undefined) {
        return data_count >= min && data_count <= max;
      }

      return false;
    }) ?? barSizeStyles[0];

    resolvedConfigCache.barSize.set(cacheKey, result);
    return result;
  }, [resolvedConfigCache]);


  const getResolvedConfig = useCallback((
    base: ChartConfig["config"],
    data_count?: number,
    bar_count?: number,
    configId?: string
  ): ChartConfig["config"] => {

    // Create a comprehensive cache key for the entire resolved config
    const fullConfigKey = `${configId}-${data_count ?? 'undefined'}-${bar_count ?? 'undefined'}`;

    if (resolvedConfigCache.fullConfig.has(fullConfigKey)) {
      return resolvedConfigCache.fullConfig.get(fullConfigKey)!;
    }

    const resolvedConfig = {
      ...base,
      bar_size: resolveBarSize(base.bar_size, data_count, bar_count, configId),
      x_axis_top: {
        ...base.x_axis_top,
        tick: {
          ...base.x_axis_top.tick,
          ...resolveXAxisTickStyle(base.x_axis_top.tick.tick_styles, bar_count, configId!, "top"),
        }
      },
      x_axis_bottom: {
        ...base.x_axis_bottom,
        tick: {
          ...base.x_axis_bottom.tick,
          ...resolveXAxisTickStyle(base.x_axis_bottom.tick.tick_styles, bar_count, configId!, "bottom"),
        }
      },
      y_axis_left: {
        ...base.y_axis_left,
        tick: {
          ...base.y_axis_left.tick,
          ...resolveYAxisTickStyle(base.y_axis_left.tick.tick_styles, data_count, configId!, "left"),
        }
      },
      y_axis_right: {
        ...base.y_axis_right,
        tick: {
          ...base.y_axis_right.tick,
          ...resolveYAxisTickStyle(base.y_axis_right.tick.tick_styles, data_count, configId!, "right"),
        }
      },
      resolved_data_label: resolveDataLabel(base.data_labels, bar_count, configId!)
    };

    resolvedConfigCache.fullConfig.set(fullConfigKey, resolvedConfig);
    return resolvedConfig;
  }, [resolveXAxisTickStyle, resolveYAxisTickStyle, resolveDataLabel, resolveBarSize, resolvedConfigCache]);

  const deepMerge = useCallback((target: any, source: any): any => {
    if (!source || typeof source !== 'object') return target;
    if (!target || typeof target !== 'object') return source;

    const result = { ...target };

    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (typeof source[key] === 'object' && !Array.isArray(source[key]) && source[key] !== null) {
          result[key] = deepMerge(result[key] || {}, source[key]);
        } else {
          result[key] = source[key];
        }
      }
    }

    return result;
  }, []);

  const getChartConfig = useCallback(async (
    chart_type: string,
    view_type: string,
    grid: string,
    data_count?: number,
    bar_count?: number
  ): Promise<ChartConfig["config"] | undefined> => {
    try {
      const defaultChartConfigs = await loadConfigFile('default', '');

      const defaultConfig = defaultChartConfigs.find(
        cfg => cfg.chart_type === chart_type && cfg.view_type === 'default'
      ) || defaultChartConfigs.find(cfg => cfg.chart_type === chart_type);


      let mergedConfig = defaultConfig?.config || {};
      let actualViewType = view_type;
      let actualGrid = grid;

      if (view_type !== 'default') {
        const gridChartConfigs = await loadConfigFile(view_type, grid);

        if (gridChartConfigs) {
          // Find the specific chart configuration in grid config
          const gridConfig = gridChartConfigs.find(
            cfg => cfg.chart_type === chart_type && cfg.view_type === view_type
          ) || gridChartConfigs.find(cfg => cfg.chart_type === chart_type);

          if (gridConfig?.config) {
            // Merge grid config with default config
            mergedConfig = deepMerge(defaultConfig.config, gridConfig.config);
          } else {
            console.warn(`Grid configuration not found for chart type: \${chart_type}, using default`);
          }
        } else {
          console.warn(`Grid configuration file not found for \${view_type}/\${grid}, using default`);
          // Update actual parameters to reflect fallback to default
          actualViewType = 'default';
          actualGrid = '';
        }
      }

      const configId = getConfigFingerprint(actualViewType, actualGrid, chart_type);
      return getResolvedConfig(mergedConfig, data_count, bar_count, configId);

    } catch (error) {
      console.error(`Error loading chart configuration:`, error);
      return undefined;
    }
  }, [getResolvedConfig, loadConfigFile, getConfigFingerprint, deepMerge]);

  return (
    <ChartConfigContext.Provider value={{ getChartConfig }}>
      {children}
    </ChartConfigContext.Provider>
  );
};

export const useChartConfig = () => {
  const context = useContext(ChartConfigContext);
  if (!context) throw new Error("useChartConfig must be used within ChartConfigProvider");
  return context;
};